import type { MentionExpandContext, MentionUser } from "@/components/AtMention/types";
import { splitMentionName } from "@/components/AtMention/mentionHelpers";
import { TYPENAME_TO_REFERENCEABLE_TYPE } from "@/features/global/components/NotesTable/mutations";
import {
  getReferenceIcon,
  getReferenceSupportingText,
  getReferenceTypeLabel
} from "@/features/global/components/ReferencesTable/types";
import type { EventSearchNode } from "@/pages/tasks/[taskId]/_hooks/eventsForReferenceSearchQuery";
import type {
  ReferenceLookupContext,
  ReferencePickerItem
} from "@/pages/tasks/[taskId]/_hooks/useReferencePickerItems";
import type { QuickAddFacilitySearchNode, QuickAddPersonSearchNode } from "./queries";

export const QUICK_ADD_MENTION_OVERLAY = {
  prompt: "Mention a teammate or attach a person, facility, event, or note",
  empty: "No matches found",
  ariaLabel: "Mention a teammate or attach a record"
};

const PENDING_REGISTRATION_CREATED_REASONS = new Set(["pending_associate", "pending_affiliate"]);
const UNEMPLOYED_PROGRAM_STATUSES = new Set(["ACTIVE", "SUSPENDED"]);

export function classificationDisplay(name?: string | null, description?: string | null): string | undefined {
  const code = name?.trim();
  if (!code) return undefined;
  const detail = description?.trim();
  if (!detail || detail === code) return code;
  return `${code} (${detail})`;
}

export function programStatusDisplay(status?: string | null, createdReason?: string | null): string | undefined {
  if (!status) return undefined;
  if (status === "ACTIVE" && createdReason && PENDING_REGISTRATION_CREATED_REASONS.has(createdReason)) {
    return "Pending";
  }
  return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
}

export function hasPersonProgramStatus(status?: string | null): boolean {
  return Boolean(status?.trim());
}

function personFacilityName(node: QuickAddPersonSearchNode): string | undefined {
  if (node.primaryFacility?.name) return node.primaryFacility.name;
  const status = node.latestProgram?.status;
  if (status && UNEMPLOYED_PROGRAM_STATUSES.has(status)) return "Unemployed";
  return undefined;
}

export function teammateMention(user: MentionUser): MentionUser {
  return { ...user, kind: user.kind ?? "teammate", selectAction: "insert" };
}

export function mergePersonMembership(
  people: QuickAddPersonSearchNode[],
  membershipNodes: QuickAddPersonSearchNode[] | null | undefined
): QuickAddPersonSearchNode[] {
  if (!membershipNodes?.length) return people;
  const byPgaId = new Map<string, QuickAddPersonSearchNode>();
  for (const node of membershipNodes) {
    const key = node.pgaId?.trim();
    if (key) byPgaId.set(key, node);
  }
  return people.map((person) => {
    const membership = person.pgaId ? byPgaId.get(person.pgaId) : undefined;
    if (!membership) return person;
    return { ...person, latestProgram: membership.latestProgram };
  });
}

export function mapQuickAddPersonNodeToMention(
  node: QuickAddPersonSearchNode,
  options?: { dropBlankProgramStatus?: boolean }
): MentionUser | null {
  const pgaId = node.pgaId?.trim();
  if (!pgaId) return null;
  if (options?.dropBlankProgramStatus && !hasPersonProgramStatus(node.latestProgram?.status)) return null;
  const { firstName, lastName } = splitMentionName(node.displayName);
  const facilityName = personFacilityName(node);
  const sectionName = node.primarySection?.name ?? undefined;
  const state = node.primaryFacility?.addresses?.find((address) => address.state)?.state ?? undefined;
  return {
    id: `expand:Person:${pgaId}`,
    firstName,
    lastName,
    email: node.email?.trim() || "",
    photo: node.profilePhoto ?? undefined,
    kind: "person",
    subtitle: [sectionName, facilityName].filter(Boolean).join(" · ") || undefined,
    iconName: "person",
    selectAction: "expand",
    expandContext: { type: "Person", id: pgaId },
    sectionName,
    state,
    city: node.primaryFacility?.addresses?.find((address) => address.city)?.city ?? undefined,
    facilityName,
    pgaId,
    classification: classificationDisplay(
      node.latestProgram?.band?.classification?.name,
      node.latestProgram?.band?.classification?.description
    ),
    programStatus: programStatusDisplay(node.latestProgram?.status, node.latestProgram?.band?.createdReason)
  };
}

export function mapQuickAddFacilityNodeToMention(node: QuickAddFacilitySearchNode): MentionUser | null {
  if (!node.id) return null;
  const { firstName, lastName } = splitMentionName(node.name);
  const location = [node.mainAddress?.city, node.mainAddress?.state].filter(Boolean).join(", ");
  return {
    id: `FACILITY:${node.id}`,
    firstName,
    lastName,
    email: "",
    kind: "facility",
    subtitle: location || node.section?.name || undefined,
    iconName: "store",
    selectAction: "insert",
    expandContext: {
      type: "Facility",
      id: node.id,
      ...(node.legacyId ? { legacyId: node.legacyId } : {}),
      name: node.name,
      city: node.mainAddress?.city ?? undefined,
      state: node.mainAddress?.state ?? undefined
    },
    sectionName: node.section?.name ?? undefined,
    city: node.mainAddress?.city ?? undefined,
    state: node.mainAddress?.state ?? undefined,
    facilityName: node.name
  };
}

export function mapEventToMention(event: EventSearchNode): MentionUser {
  const { firstName, lastName } = splitMentionName(event.title);
  const subtitle = [event.facility?.name, event.city, event.state].filter(Boolean).join(" · ") || undefined;
  return {
    id: `EVENT:${event.id}`,
    firstName,
    lastName,
    email: "",
    kind: "event",
    subtitle,
    iconName: "event",
    selectAction: "insert",
    expandContext: {
      type: "Event",
      id: event.id,
      slug: event.slug,
      title: event.title,
      status: event.status,
      startsAt: event.startsAt
    },
    state: event.state
  };
}

export function mapPickerItemToMention(item: ReferencePickerItem): MentionUser | null {
  const referenceableType = TYPENAME_TO_REFERENCEABLE_TYPE[item.__typename];
  if (!referenceableType) return null;
  const { firstName, lastName } = splitMentionName(item.displayName);
  const supporting = getReferenceSupportingText(item);
  const subtitle = supporting.split("\n")[0] || getReferenceTypeLabel(item.__typename, item.notableType);
  const kind =
    item.__typename === "CRMPerson"
      ? "person"
      : item.__typename === "CRMTaskFacility"
        ? "facility"
        : item.__typename === "CRMEvent"
          ? "event"
          : "reference";
  return {
    id: `${referenceableType}:${item.id}`,
    firstName,
    lastName,
    email: "",
    kind,
    subtitle,
    iconName: getReferenceIcon(item.__typename),
    selectAction: "insert",
    sectionName: undefined,
    city: item.facilityCity,
    state: item.facilityState
  };
}

export function facilityMentionFromPersonNode(node: QuickAddPersonSearchNode): MentionUser | null {
  const facilityId = node.primaryFacility?.id;
  const facilityName = node.primaryFacility?.name;
  if (!facilityId || !facilityName) return null;
  const { firstName, lastName } = splitMentionName(facilityName);
  const city = node.primaryFacility?.addresses?.find((address) => address.city)?.city;
  const state = node.primaryFacility?.addresses?.find((address) => address.state)?.state;
  const location = [city, state].filter(Boolean).join(", ");
  return {
    id: `FACILITY:${facilityId}`,
    firstName,
    lastName,
    email: "",
    kind: "facility",
    subtitle: location || node.primarySection?.name || undefined,
    iconName: "store",
    selectAction: "insert",
    expandContext: {
      type: "Facility",
      id: facilityId,
      name: facilityName,
      city: city ?? undefined,
      state: state ?? undefined
    },
    sectionName: node.primarySection?.name ?? undefined,
    city: city ?? undefined,
    state: state ?? undefined,
    facilityName
  };
}

export function toLookupContext(context: MentionExpandContext): ReferenceLookupContext {
  return context;
}
